import { PROVIDERS } from '@exodus/fiat-client';
import currencies from '@exodus/fiat-currencies';
import {
  DYNAMIC_EVM_CHAIN_NAME_TO_ASSET_NAME,
  assetPlugins,
  combinedAssetsList,
} from '@passkeys/assets';
import ms from 'ms';

import { UI_CONFIG_DEFINITION } from '../constants/ui-config.js';
import { getAppConfig, getAppId } from '../utils/app-config.js';
import { mockedAddresses } from './debug/mocks.js';

const appName = getAppId();

const defaultMultiChainAssets = combinedAssetsList.map((asset) => asset.name);

// List of asset names that are not production ready yet (i.e. no signer support).
const unsupportedAssetNames: Set<string> = new Set([
  //
]);

const defaultAvailableAssets = [
  ...Object.keys(assetPlugins),
  ...defaultMultiChainAssets,
].filter((name) => !unsupportedAssetNames.has(name));

const appAdditionalEnabledAssets =
  getAppConfig().assets?.additionalEnabled?.map((name: string) => {
    // `name` is either a dynamic EVM chain name or a built-in asset name.
    const dynamicEvmAssetName = DYNAMIC_EVM_CHAIN_NAME_TO_ASSET_NAME[name];
    return dynamicEvmAssetName ?? name;
  }) ?? [];

const defaultEnabledAssets = [
  'bitcoin',
  'ethereum',
  'solana',
  ...appAdditionalEnabledAssets,
];

const defaultConfig = {
  addressProvider: {
    mockedAddresses,
    mockable: __BUILD_METADATA_LOCAL__,
  },
  airdrop: {
    baseUrl: 'https://minze.a.exodus.io/airdrops',
  },
  analyticsTracker: {
    segment: {
      apiKey: __SEGMENT_API_KEY__,
    },
  },
  assetsModule: {
    assetsConfig: {
      solana: { monitorInterval: 5000 },
      // These testnets have custom tokens disabled by default. We want them enabled for passkeys.
      ethereumarbonesepolia: { customTokens: false }, // THIS IS NOT ENABLED ON CTR YET
      mantlesepolia: { customTokens: true },
      ethereumholesky: { customTokens: true },
    },
  },
  availableAssets: {
    defaultAvailableAssetNames: defaultAvailableAssets,
  },
  fiatBalances: {
    balanceFields: ['balance'],
    nonDustAssets: {
      defaultBalanceThresholdUsd: currencies.USD.defaultUnit(0.5), // auto enable USD fiat balance threshold.
    },
  },
  optimisticFiatBalances: {
    balanceFields: ['balance'],
  },
  enabledAssets: {
    defaultEnabledAssetsList: defaultEnabledAssets,
  },
  autoEnableAssetsPlugin: {
    alwaysAutoEnable: false, // if the user disabled a token, keep it disabled on restart. This includes user disabled testnets
    throttleInterval: 500,
  },
  remoteConfig: {
    remoteConfigUrl: `https://remote-config.exodus.io/v1/passkeys.json`,
  },
  trackNonDustAssetsNamesPlugin: {
    balanceThresholdsUsd: {},
  },
  uiConfig: UI_CONFIG_DEFINITION,
  fiatRamp: {
    FIAT_SERVER_URL: 'https://fiat.a.exodus.io/',
    provider: {
      [PROVIDERS.blockchain]: {
        API_URL: 'https://api.blockchain.info',
        API_KEY: '5e352495-d1d7-4e4e-8cba-7d54fdbcca32', // not a secret - this is a client side identifier
        URL: 'https://blockchain.com/pay/widget',
      },
    },
  },
  faucet: {
    txWatchTimeout: ms('60s'),
  },
  faucetClient: {
    apiUrl: 'https://testnet-exchange-provider-d.a.exodus.io',
  },
  fiatOrdersMonitor: {
    fetchInterval: ms('5s'),
    maxAge: ms('4d'),
  },
  orders: {
    optimisticActivityEnabled: true,
    supportedSvcs: [],
    interval: ms('10s'),
    appName,
    appVersion: __APP_VERSION__,
    baseUrl: 'https://exchange.exodus.io/v3',
    txTypes: {
      PRE_SETUP: 'preSetupTransaction',
      SETUP: 'setupTransaction',
      SWAP: 'swapTransaction',
      CLEANUP: 'cleanupTransaction',
      DEPOSIT: 'depositTransaction',
      PAYOUT: 'payoutTransaction',
    },
  },
  exchange: {
    services: {
      assets: {
        appName,
        appVersion: __APP_VERSION__,
        baseUrl: 'https://exchange.exodus.io/v3',
      },
      orders: {
        appName,
        appVersion: __APP_VERSION__,
        baseUrl: 'https://exchange.exodus.io/v3',
      },
    },
    providers: {
      exodus: {
        appName,
        appVersion: __APP_VERSION__,
        baseUrl: 'https://exchange.exodus.io/v3',
        enabled: true,
        options: {
          extraFeatures: ['pid'],
          bypassProvidersValidation: true,
        },
      },
      lifi: {
        baseUrl: 'https://li.quest/v1',
        enabled: true,
        apiKeys: {
          default:
            '06736151-26d9-4583-a984-8b622fe7c4b7.2aebe520-6838-4940-b6a0-f29f601b59c9', // not a secret - this is a client side identifier
        },
      },
      jupiter: {
        enabled: true,
      },
    },
  },
  nftsMonitor: {
    autoApproveOnImport: false,
    includeSpamNfts: false,
    useBatchMonitor: true,
  },
};

export default defaultConfig;
